import React, { useState, useMemo } from 'react';
import { IoChevronDown, IoChevronForward, IoClose } from 'react-icons/io5';

import { Watchlist } from '@benzinga/watchlist-manager';
import { Tooltip } from '@benzinga/core-ui';
import { Toggler, sortRows } from './index';

interface NotificationsOverviewProps {
  rows: Watchlist[];
  onSelectWatchlist: (watchlist: Watchlist) => void;
  onShowRemoveWatchlist: (watchlist: Watchlist) => void;
  onUpdateWatchlistAlert: (watchlist: Watchlist, val: boolean) => Promise<void>;
}

const columns = [
  {
    title: 'Daily briefing',
    id: 'emailSummary',
    type: 'toggle',
    sortable: true,
    description: 'Twice a day receive a digest of everything you need to know about your stocks',
  },
  {
    title: 'News',
    id: 'sendRealtime',
    type: 'count',
    sortable: false,
    description: 'Real-time alerts sent via email & push notifications (download the Benzinga app)',
  },
  {
    title: 'Press',
    id: 'isPR',
    type: 'count',
    sortable: false,
    description: 'Company issued announcements',
  },
  {
    title: 'SEC',
    id: 'isSEC',
    type: 'count',
    sortable: false,
    description: 'Company issued legal filings',
  },
];

const SortCaret = ({ active, direction }: { active: boolean; direction?: 'asc' | 'desc' }) => (
  <IoChevronDown
    className={`size-3.5 shrink-0 transition-transform ${
      active ? 'opacity-100' : 'opacity-30'
    } ${active && direction === 'asc' ? 'rotate-180' : ''}`}
  />
);

const AlertCountBadge = ({ count }: { count: string | null }) => {
  if (!count) {
    return (
      <span className="inline-flex min-w-10 justify-center rounded-md bg-slate-100 px-2 py-0.5 text-xs font-bold text-slate-500">
        Off
      </span>
    );
  }

  return (
    <span
      className={`inline-flex min-w-10 justify-center rounded-md px-2 py-0.5 text-xs font-bold ${
        count === 'All' ? 'bg-bzblue-100 text-bzblue-800' : 'bg-slate-100 text-slate-700'
      }`}
    >
      {count}
    </span>
  );
};

const NotificationsOverview: React.FC<NotificationsOverviewProps> = ({
  rows,
  onSelectWatchlist,
  onShowRemoveWatchlist,
  onUpdateWatchlistAlert,
}) => {
  const [sortType, setSortType] = useState<string | undefined>(undefined);
  const [sortDir, setSortDir] = useState<'asc' | 'desc' | undefined>(undefined);

  const sortedRows = useMemo(() => {
    if (!sortType || !sortDir) {
      return rows;
    }
    return sortRows(rows, sortType, sortDir);
  }, [rows, sortType, sortDir]);

  const sortColumn = (columnId: string) => {
    if (columnId !== sortType) {
      setSortType(columnId);
      setSortDir('asc');
      return;
    }

    if (sortDir === 'asc') {
      setSortDir('desc');
    } else {
      setSortType(undefined);
      setSortDir(undefined);
    }
  };

  const getAlertCount = (alert: string, watchlist: Watchlist) => {
    const alertCount = watchlist.symbols.map(symbol => symbol[alert]).filter(val => val === 1).length;
    const watchlistCount = watchlist.symbols.length;

    if (alertCount === 0 || watchlistCount === 0) {
      return null;
    }
    if (alertCount === watchlistCount) {
      return 'All';
    }
    return `${alertCount}/${watchlistCount}`;
  };

  if (rows.length === 0) {
    return (
      <div className="rounded-xl border border-dashed border-slate-200 bg-slate-50 px-4 py-10 text-center">
        <p className="text-sm font-semibold text-slate-800">No watchlists found</p>
        <p className="mt-1 text-sm text-slate-500">Create a watchlist to configure alerts.</p>
      </div>
    );
  }

  return (
    <div>
      <div className="space-y-2 sm:hidden">
        {(sortedRows as Watchlist[]).map(row => (
          <div key={row.watchlistId} className="rounded-xl border border-slate-200 bg-white p-3.5">
            <div className="mb-3 flex items-center justify-between gap-2">
              <h3 className="min-w-0 truncate text-base font-bold text-slate-950">{row.name}</h3>
              <button
                type="button"
                aria-label={`Delete ${row.name}`}
                className="inline-flex size-8 shrink-0 items-center justify-center rounded-lg text-slate-400 hover:bg-red-50 hover:text-red-600"
                onClick={() => onShowRemoveWatchlist(row)}
              >
                <IoClose size={16} />
              </button>
            </div>

            <div className="mb-3 flex items-center justify-between gap-3 rounded-lg bg-slate-50 px-3 py-2.5">
              <span className="text-sm font-semibold text-slate-800">Daily briefing</span>
              <Toggler enabled={row.emailSummary} onChange={val => onUpdateWatchlistAlert(row, val)} />
            </div>

            <button
              type="button"
              className="w-full rounded-xl border border-slate-200 bg-white p-3 text-left transition-colors hover:border-bzblue-300 hover:bg-bzblue-50/40 active:scale-[0.99]"
              onClick={() => onSelectWatchlist(row)}
            >
              <div className="mb-2.5 flex items-center justify-between gap-2">
                <span className="text-sm font-semibold text-slate-800">Ticker alerts</span>
                <span className="inline-flex items-center gap-0.5 text-xs font-semibold text-bzblue-700">
                  Edit
                  <IoChevronForward className="size-3.5" />
                </span>
              </div>
              <div className="grid grid-cols-3 gap-3">
                {columns
                  .filter(column => column.type === 'count')
                  .map(column => (
                    <div
                      key={`${row.watchlistId}-${column.id}`}
                      className="rounded-lg border border-slate-100 bg-slate-50 px-2 py-2.5 text-center"
                    >
                      <div className="mb-1.5 text-[10px] font-bold uppercase tracking-wide text-slate-500">
                        {column.title}
                      </div>
                      <AlertCountBadge count={getAlertCount(column.id, row)} />
                    </div>
                  ))}
              </div>
            </button>
          </div>
        ))}
      </div>

      <div className="hidden overflow-x-auto sm:block">
        <div className="overflow-hidden rounded-xl border border-slate-200">
          <table className="w-full min-w-[560px] border-collapse text-left">
            <thead>
              <tr className="border-b border-slate-200 bg-slate-50">
                <th className="sticky left-0 z-10 w-[40%] bg-slate-50 px-4 py-2.5 text-left">
                  <button
                    type="button"
                    className={`inline-flex items-center gap-1 text-left text-[11px] font-bold uppercase tracking-wide ${
                      sortType === 'name' ? 'text-bzblue-800' : 'text-slate-500 hover:text-slate-800'
                    }`}
                    onClick={() => sortColumn('name')}
                  >
                    Watchlist
                    <SortCaret active={sortType === 'name'} direction={sortDir} />
                  </button>
                </th>
                {columns.map(column => (
                  <th key={column.id} className="px-3 py-2.5 text-center">
                    <button
                      type="button"
                      disabled={!column.sortable}
                      className={`inline-flex items-center justify-center gap-1 text-[11px] font-bold uppercase tracking-wide disabled:cursor-default ${
                        sortType === column.id ? 'text-bzblue-800' : 'text-slate-500 hover:text-slate-800'
                      }`}
                      onClick={() => {
                        if (column.sortable) sortColumn(column.id);
                      }}
                    >
                      <Tooltip content={column.description}>
                        <span>{column.title}</span>
                      </Tooltip>
                      {column.sortable && <SortCaret active={sortType === column.id} direction={sortDir} />}
                    </button>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {(sortedRows as Watchlist[]).map(row => (
                <tr key={row.watchlistId} className="group border-b border-slate-100 last:border-b-0 hover:bg-slate-50/80">
                  <td className="sticky left-0 z-10 bg-white px-4 py-3.5 group-hover:bg-slate-50/80">
                    <div className="flex items-center justify-between gap-2">
                      <button
                        type="button"
                        className="truncate text-left text-sm font-bold text-slate-950 hover:text-bzblue-800"
                        onClick={() => onSelectWatchlist(row)}
                      >
                        {row.name}
                      </button>
                      <button
                        type="button"
                        aria-label={`Delete ${row.name}`}
                        className="rounded p-1 text-slate-300 opacity-0 transition-opacity hover:bg-red-50 hover:text-red-600 group-hover:opacity-100"
                        onClick={() => onShowRemoveWatchlist(row)}
                      >
                        <IoClose size={15} />
                      </button>
                    </div>
                  </td>
                  {columns.map(column => (
                    <td key={`${row.watchlistId}-${column.id}`} className="px-3 py-3.5 text-center">
                      {column.id === 'emailSummary' ? (
                        <div className="flex justify-center">
                          <Toggler enabled={row.emailSummary} onChange={val => onUpdateWatchlistAlert(row, val)} />
                        </div>
                      ) : (
                        <AlertCountBadge count={getAlertCount(column.id, row)} />
                      )}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default NotificationsOverview;
